import React from "react";
import PropTypes from "prop-types";
import { Tabs, Tab, Box } from "@mui/material";
import { colors } from "../../../../theme";
import CommonChip from "../data-display/CommonChip";

/**
 * CommonTab Component
 * A styled single tab supporting labels, icons, and count/status badges.
 */
export const CommonTab = ({
  label,
  count,
  badge,
  chipVariant = "neutral",
  chipColor,
  chipBg,
  icon,
  iconPosition = "start",
  pill = true,
  sx = {},
  ...props
}) => {
  const badgeValue = count !== undefined ? count : badge;

  const renderLabel = () => {
    if (badgeValue === undefined && !icon && typeof label === "object") {
      return label;
    }

    if (badgeValue === undefined && !icon) {
      return label;
    }

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {icon && iconPosition === "start" && icon}
        {typeof label === "string" ? <span>{label}</span> : label}
        {icon && iconPosition === "end" && icon}
        {badgeValue !== undefined && (
          <CommonChip
            label={badgeValue}
            variant={chipVariant}
            color={chipColor}
            backgroundColor={chipBg}
            size="small"
            pill={pill}
          />
        )}
      </Box>
    );
  };

  return (
    <Tab
      label={renderLabel()}
      sx={{
        minHeight: "44px",
        textTransform: "none",
        fontWeight: 600,
        fontSize: "14px",
        color: colors.slate[500],
        py: 1,
        px: 2,
        transition: "all 0.2s ease-in-out",
        "&.Mui-selected": {
          color: colors.primary.main,
          fontWeight: 700,
        },
        "&:hover": {
          color: colors.slate[800],
        },
        ...sx,
      }}
      {...props}
    />
  );
};

CommonTab.propTypes = {
  label: PropTypes.node,
  count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  badge: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  chipVariant: PropTypes.string,
  chipColor: PropTypes.string,
  chipBg: PropTypes.string,
  icon: PropTypes.element,
  iconPosition: PropTypes.oneOf(["start", "end", "top", "bottom"]),
  pill: PropTypes.bool,
  sx: PropTypes.object,
};

/**
 * CommonTabs Component
 * Reusable tab bar component adhering to the AutoBET theme tokens and styling.
 * Supports declarative `items` configuration or explicit `children`.
 */
const CommonTabs = ({
  value,
  onChange,
  items,
  children,
  variant = "scrollable",
  scrollButtons = "auto",
  allowScrollButtonsMobile = true,
  minHeight = "44px",
  indicatorColor = colors.primary.main,
  activeColor = colors.primary.main,
  textColor = colors.slate[500],
  sx = {},
  tabSx = {},
  ...props
}) => {
  const handleChange = (event, newValue) => {
    if (onChange) {
      if (onChange.length === 1) {
        onChange(newValue);
      } else {
        onChange(event, newValue);
      }
    }
  };

  return (
    <Tabs
      value={value}
      onChange={handleChange}
      variant={variant}
      scrollButtons={scrollButtons}
      allowScrollButtonsMobile={allowScrollButtonsMobile}
      sx={{
        minHeight,
        "& .MuiTabs-indicator": {
          backgroundColor: indicatorColor,
          height: "3px",
          borderRadius: "3px 3px 0 0",
        },
        "& .MuiTab-root": {
          minHeight,
          textTransform: "none",
          fontWeight: 600,
          fontSize: "14px",
          color: textColor,
          py: 1,
          px: 2,
          transition: "all 0.2s ease-in-out",
          "&.Mui-selected": {
            color: activeColor,
            fontWeight: 700,
          },
          "&:hover": {
            color: colors.slate[800],
          },
          ...tabSx,
        },
        ...sx,
      }}
      {...props}
    >
      {items && items.length > 0
        ? items.map((item) => (
            <CommonTab
              key={item.value ?? item.label}
              value={item.value}
              label={item.label}
              count={item.count}
              badge={item.badge}
              chipVariant={item.chipVariant}
              chipColor={item.chipColor}
              chipBg={item.chipBg}
              icon={item.icon}
              iconPosition={item.iconPosition}
              disabled={item.disabled}
              pill={item.pill}
              sx={{ ...tabSx, ...item.sx }}
            />
          ))
        : children}
    </Tabs>
  );
};

CommonTabs.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any.isRequired,
      label: PropTypes.node.isRequired,
      count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      badge: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      chipVariant: PropTypes.string,
      chipColor: PropTypes.string,
      chipBg: PropTypes.string,
      icon: PropTypes.element,
      iconPosition: PropTypes.string,
      disabled: PropTypes.bool,
      pill: PropTypes.bool,
      sx: PropTypes.object,
    })
  ),
  children: PropTypes.node,
  variant: PropTypes.string,
  scrollButtons: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  allowScrollButtonsMobile: PropTypes.bool,
  minHeight: PropTypes.string,
  indicatorColor: PropTypes.string,
  activeColor: PropTypes.string,
  textColor: PropTypes.string,
  sx: PropTypes.object,
  tabSx: PropTypes.object,
};

export default CommonTabs;
